import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta, shortDate } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import { ListSkeleton } from "@/components/Skeletons";
import { cleanNarrative } from "./wevaUtils";
import { Sparkles, MessageCircle, FileText, CalendarDays, ChevronRight, Info } from "lucide-react";

function ActionCard({ to, icon: Icon, title, subtitle, testId }) {
  return (
    <Link to={to} data-testid={testId}
      className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:shadow-md">
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
        <Icon className="h-6 w-6 text-primary" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-base text-foreground">{title}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

export default function Weva() {
  const t = useT();
  usePageMeta(t("weva"));
  const [reports, setReports] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/weva/reports");
      setReports(Array.isArray(data) ? data : []);
    } catch { setReports([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="weva-hub">
      <PageHeader eyebrow={t("nav.experience.journey")} title={t("weva")} subtitle={t("weva_subtitle")} />

      <div className="flex items-start gap-2.5 rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="weva-freemium-notice">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p className="text-sm text-muted-foreground">{t("weva_freemium_notice")}</p>
      </div>

      <div className="space-y-3">
        <ActionCard to="/app/weva/chat" icon={MessageCircle} title={t("weva_action_chat")} subtitle={t("weva_action_chat_sub")} testId="weva-open-chat" />
        <ActionCard to="/app/weva/reports" icon={FileText} title={t("weva_action_reports")} subtitle={t("weva_action_reports_sub")} testId="weva-open-reports" />
        <ActionCard to="/app/weva/digests" icon={CalendarDays} title={t("weva_action_digests")} subtitle={t("weva_action_digests_sub")} testId="weva-open-digests" />
      </div>

      {reports === null ? (
        <ListSkeleton rows={3} />
      ) : reports.length > 0 ? (
        <div className="space-y-2">
          <h2 className="font-display text-lg text-foreground">{t("weva_tab_history")}</h2>
          {reports.slice(0, 5).map((r) => (
            <Link key={r.id} to={`/app/weva/report/${r.id}`} data-testid={`hist-report-${r.id}`}
              className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent/30">
              <Sparkles className="h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm text-foreground">{cleanNarrative(r.narrative) || t("weva_report_fallback_title")}</p>
                <p className="mt-1 text-xs text-muted-foreground">{shortDate(r.created_at)}</p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}
